import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  fetchGoogleContactSync,
  runGoogleContactSync,
  setGoogleContactSync,
} from "../../lib/googleContacts";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";

/**
 * Every lead in your own Google Contacts, and so on an Android phone.
 *
 * Sits under Gmail on the Integrations screen because it rides on that
 * connection: the account you connect there is the account your phone reads
 * its contacts from. Shown only to the people holding
 * `profiles.syncs_google_contacts` — Samuel, from 2026-09-10 — and the database
 * refuses the switch to anybody else regardless (ADR 0128).
 *
 * What it has to say, because a switch does not: that somebody already in
 * your phone is added to rather than copied, and that turning it off does not
 * take anything back out.
 */
export function GoogleContactsSync() {
  const { profile, profileId } = useSession();
  if (profile?.syncs_google_contacts !== true || !profileId) return null;
  return <GoogleContactsCard profileId={profileId} />;
}

function GoogleContactsCard({ profileId }: { profileId: string }) {
  const state = useAsync(() => fetchGoogleContactSync(profileId), [profileId]);
  const [busy, setBusy] = useState<"switch" | "run" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(kind: "switch" | "run", work: () => Promise<unknown>) {
    setBusy(kind);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      await state.reload();
      setBusy(null);
    }
  }

  const s = state.data;

  return (
    <>
      <h3 style={{ marginTop: 28 }}>{t("Leads in my phone’s contacts")}</h3>
      <ErrorNote error={state.error ?? error ?? s?.lastError ?? null} />

      {state.loading || !s ? (
        <div className="stub">{t("Checking…")}</div>
      ) : !s.connected ? (
        <p className="na-hint" style={{ maxWidth: 560 }}>
          {t(
            "Connect Gmail above first. Your phone gets its contacts from that Google account, so that is where the leads go.",
          )}
        </p>
      ) : !s.allowsContacts ? (
        <p className="na-hint" style={{ maxWidth: 560 }}>
          {t("Press")} <strong>{t("Reconnect")}</strong>{" "}
          {t(
            "above, and when Google asks, allow Roof Board to see and change your contacts. Then come back here.",
          )}
        </p>
      ) : (
        <>
          <div className="rrow">
            <span className="k">{t("Put every lead in my contacts")}</span>
            <span className="v">
              <span className={`badge ${s.enabled ? "approved" : "draft"}`}>
                {s.enabled ? t("on") : t("off")}
              </span>
            </span>
          </div>

          <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
            {t(
              "Every lead goes into the contacts for {email}, under a label called Roof Board, with their address, a link to their CompanyCam photos and a link back here. Someone already in your contacts gets added to, not copied — their name and notes stay as you wrote them.",
              { email: s.accountEmail ?? "" },
            )}
          </p>

          <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
            {t(
              "Turning this off stops new leads going in. It does not take out the ones already there. A contact you delete from your phone is not put back.",
            )}
          </p>

          <div className="actions" style={{ marginTop: 12 }}>
            <button
              className="addrow"
              disabled={busy !== null}
              onClick={() =>
                void act("switch", () => setGoogleContactSync(!s.enabled))
              }
            >
              {busy === "switch"
                ? t("Saving…")
                : s.enabled
                  ? t("Stop adding leads")
                  : t("Put leads in my contacts")}
            </button>
            {s.enabled && (
              <button
                className="ghost"
                disabled={busy !== null}
                onClick={() => void act("run", runGoogleContactSync)}
              >
                {busy === "run" ? t("Working…") : t("Sync now")}
              </button>
            )}
          </div>

          {s.lastRunAt && s.lastResult && (
            <LastRun at={s.lastRunAt} result={s.lastResult} />
          )}
        </>
      )}
    </>
  );
}

function LastRun({
  at,
  result,
}: {
  at: string;
  result: NonNullable<
    Awaited<ReturnType<typeof fetchGoogleContactSync>>["lastResult"]
  >;
}) {
  return (
    <div style={{ marginTop: 16, maxWidth: 560 }}>
      <p className="na-hint">
        {t(
          "Last run {when}: {added} added, {joined} added to a contact you already had, {updated} updated.",
          {
            when: new Date(at).toLocaleString(),
            added: result.added,
            joined: result.joined,
            updated: result.updated,
          },
        )}
      </p>
      {result.unfinished && <p className="na-hint">{result.unfinished}</p>}

      {result.skipped.length > 0 && (
        <>
          <h4 style={{ margin: "14px 0 6px" }}>
            {t("Left out, for you to decide")}
          </h4>
          {result.skipped.map((item) => (
            <div className="rrow" key={`${item.name}-${item.reason}`}>
              <span className="k">{item.name}</span>
              <span className="v">{item.reason}</span>
            </div>
          ))}
        </>
      )}

      {result.sharedNumbers.length > 0 && (
        <>
          <h4 style={{ margin: "14px 0 6px" }}>
            {t("Entered twice in Roof Board")}
          </h4>
          <p className="na-hint">
            {t(
              "These share a phone number or email, so each gets its own contact. Merge them in Roof Board, not in your phone.",
            )}
          </p>
          {result.sharedNumbers.map((item) => (
            <div className="rrow" key={item.key}>
              <span className="k">{item.names.join(", ")}</span>
              <span className="v">{item.key}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
